import React, { useState, useEffect } from 'react';
import './MarketQuotes.css';
import {
  Search, TrendingUp, TrendingDown, Activity, BarChart3, DollarSign,
  Info, Star, PieChart, Calendar, ArrowUp, ArrowDown, Target, Award,
  Users, Volume2, Percent, Building2, Eye, AlertTriangle, CheckCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';

const StockAnalyzer = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [stockData, setStockData] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [tradingHistory, setTradingHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  
  useEffect(() => {
    const fetchStockList = async () => {
      try {
        const response = await fetch('/complete.json');
        const data = await response.json();
        const filtered = data.filter(
          (item) => item.segment === 'NSE_EQ' && item.instrument_type === 'EQ'
        );
        setStockData(filtered);
      } catch (error) {
        console.error('Error loading stock list:', error);
        // Mock data for demo
        setStockData([
          { name: 'Sun Pharma Advanced Research Company Limited', trading_symbol: 'SPARC' },
          { name: 'Reliance Industries Limited', trading_symbol: 'RELIANCE' },
          { name: 'Tata Consultancy Services Limited', trading_symbol: 'TCS' },
          { name: 'HDFC Bank Limited', trading_symbol: 'HDFCBANK' },
          { name: 'Infosys Limited', trading_symbol: 'INFY' }
        ]);
      }
    };

    fetchStockList();
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      const filtered = stockData.filter(stock =>
        stock.trading_symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStocks(filtered.slice(0, 10));
      setShowDropdown(true);
    } else {
      setFilteredStocks([]);
      setShowDropdown(false);
    }
  }, [searchTerm, stockData]);

  const handleStockSelect = async (stock) => {
    setSelectedStock(stock);
    setSearchTerm(stock.name);
    setShowDropdown(false);
    setLoading(true);

    try {
      // Replace with your actual API endpoint
       const response = await fetch(`http://127.0.0.1:5000/api/analyze/${stock.trading_symbol}`);
       const data = await response.json();
      
      // Mock delay for demo
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock analysis data based on your API structure
      const mockAnalysis = {
        Ticker: stock.trading_symbol,
        Name: stock.name,
        Industry: 'Pharmaceuticals',
        PE_Ratio: 24.50,
        PB_Ratio: 3.25,
        Market_Cap: '125.45 B',
        Revenue_Growth: '12.45%',
        Earnings_Growth: '18.75%',
        Analyst_Rating: 'BUY',
        Analyst_Count: 15,
        '52_Week_High': 1245.80,
        '52_Week_Low': 890.25,
        '50_Day_MA': 1156.45,
        '200_Day_MA': 1089.30,
        Beta: 1.25,
        Volume: 2450000,
        Average_Volume: 1850000,
        Dividend_Yield: '2.35%',
        Dividend_Payout: '28.50',
        Close: 1198.75,
        RSI: 65.40,
        MACD: 15.25,
        Signal: 12.80,
        RSI_Signal: 'Neutral',
        MACD_Signal: 'Bullish',
        Previous_Close: 1185.30,
        Price_Change: 13.45
      };

      //setAnalysisData(mockAnalysis);
      setAnalysisData(data);
      // Generate mock trading history
      const hist = Array.from({ length: 30 }, (_, i) => {
        const basePrice = mockAnalysis.Close;
        const variation = (Math.random() - 0.5) * 50;
        const close = basePrice + variation - (i * 0.5);
        const open = close + (Math.random() - 0.5) * 10;
        return {
          date: new Date(Date.now() - i * 86400000).toLocaleDateString('en-IN'),
          open: Number(open.toFixed(2)),
          high: Number((close + Math.random() * 15).toFixed(2)),
          low: Number((open - Math.random() * 10).toFixed(2)),
          close: Number(close.toFixed(2)),
          volume: Math.floor(Math.random() * 3000000) + 1000000
        };
      }).reverse();

      setTradingHistory(hist);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSignalBadgeClass = (signal) => {
    if (signal === 'Bullish' || signal === 'BUY') return 'bg-success text-white';
    if (signal === 'Bearish' || signal === 'SELL') return 'bg-danger text-white';
    if (signal === 'HOLD') return 'bg-primary text-white';
    return 'bg-warning text-dark';
  };

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num?.toString();
  };

  const toggleWatchlist = (stock) => {
    setWatchlist(prev => 
      prev.find(s => s.trading_symbol === stock.trading_symbol)
        ? prev.filter(s => s.trading_symbol !== stock.trading_symbol)
        : [...prev, stock]
    );
  };

  return (
    <div className="market-container">
      {/* Header */}
      <div className="card mb-4 fade-in">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <BarChart3 size={32} className="me-3" />
            <div>
              <h2 className="mb-1">Professional Stock Analyzer</h2>
              <small className="text-muted">Advanced Market Intelligence Platform</small>
            </div>
          </div>
          <div className="d-flex flex-column text-end">
            <small className="indicator-positive">NIFTY 50: 19,435.75 ▲ 0.85%</small>
            <small className="indicator-negative">SENSEX: 65,287.90 ▼ 0.23%</small>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="card mb-4 slide-in-left">
        <div className="card-body">
          <div className="position-relative">
            <div className="input-group">
              <span className="input-group-text">
                <Search size={20} />
              </span>
              <input
                type="text"
                className="form-control form-control-lg"
                placeholder="Search stocks (e.g., RELIANCE, TCS, HDFC, SPARC)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {showDropdown && filteredStocks.length > 0 && (
              <div className="position-absolute w-100 mt-2" style={{zIndex: 1050}}>
                <div className="card shadow-lg">
                  <div className="card-body p-0" style={{maxHeight: '300px', overflowY: 'auto'}}>
                    {filteredStocks.map((stock, idx) => (
                      <div
                        key={idx}
                        className="d-flex justify-content-between align-items-center p-3 border-bottom hover-item"
                        onClick={() => handleStockSelect(stock)}
                        style={{cursor: 'pointer'}}
                      >
                        <div>
                          <div className="fw-bold text-primary">{stock.trading_symbol}</div>
                          <small className="text-muted">{stock.name}</small>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-warning btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleWatchlist(stock);
                          }}
                        >
                          <Star size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="d-flex justify-content-center align-items-center" style={{height: '300px'}}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="text-muted">Loading comprehensive analysis...</h5>
          </div>
        </div>
      )}

      {analysisData && (
        <div className="fade-in">
          {/* Stock Header */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row align-items-start mb-4">
                <div className="col-md-8">
                  <h1 className="display-6 fw-bold text-primary mb-2">{analysisData.Name}</h1>
                  <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="fs-5 text-muted">{analysisData.Ticker}</span>
                    <span className="badge bg-info fs-6">{analysisData.Industry}</span>
                  </div>
                </div>
                <div className="col-md-4 text-end">
                  <button
                    type="button"
                    className={`btn ${watchlist.find(s => s.trading_symbol === selectedStock?.trading_symbol) ? 'btn-warning' : 'btn-outline-warning'} btn-lg`}
                    onClick={() => toggleWatchlist(selectedStock)}
                  >
                    <Star size={24} />
                    {watchlist.find(s => s.trading_symbol === selectedStock?.trading_symbol) ? ' Remove' : ' Watch'}
                  </button>
                </div>
              </div>

              <div className="row g-4">
                <div className="col-md-3">
                  <div className="data-point">
                    <div className="price-display">₹{analysisData.Close}</div>
                    <div className={`d-flex align-items-center mt-2 ${analysisData.Price_Change >= 0 ? 'indicator-positive' : 'indicator-negative'}`}>
                      {analysisData.Price_Change >= 0 ? <ArrowUp size={20} /> : <ArrowDown size={20} />}
                      <span className="ms-1 fw-bold">₹{Math.abs(analysisData.Price_Change)} ({((analysisData.Price_Change / analysisData.Previous_Close) * 100).toFixed(2)}%)</span>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="data-point">
                    <div className="data-point-label">Previous Close</div>
                    <div className="data-point-value fs-4">₹{analysisData.Previous_Close}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="data-point">
                    <div className="data-point-label">Volume</div>
                    <div className="data-point-value fs-4">{formatNumber(analysisData.Volume)}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="data-point">
                    <div className="data-point-label">Market Cap</div>
                    <div className="data-point-value fs-4">₹{analysisData.Market_Cap}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="row g-4 mb-4">
            {/* Technical Analysis */}
            <div className="col-lg-4">
              <div className="card h-100 slide-in-left">
                <div className="card-header">
                  <h6 className="d-flex align-items-center mb-0">
                    <Activity className="me-2" size={20} />
                    Technical Analysis
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>RSI (14)</span>
                        <div className="text-end">
                          <div className="fw-bold">{analysisData.RSI}</div>
                          <span className={`badge ${getSignalBadgeClass(analysisData.RSI_Signal)} mt-1`}>
                            {analysisData.RSI_Signal}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>MACD</span>
                        <div className="text-end">
                          <div className="fw-bold">{analysisData.MACD}</div>
                          <span className={`badge ${getSignalBadgeClass(analysisData.MACD_Signal)} mt-1`}>
                            {analysisData.MACD_Signal}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>50-Day MA</span>
                        <span className="fw-bold">₹{analysisData['50_Day_MA']}</span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>200-Day MA</span>
                        <span className="fw-bold">₹{analysisData['200_Day_MA']}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Valuation Metrics */}
            <div className="col-lg-4">
              <div className="card h-100">
                <div className="card-header">
                  <h6 className="d-flex align-items-center mb-0">
                    <DollarSign className="me-2" size={20} />
                    Valuation Metrics
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>P/E Ratio</span>
                        <span className="fw-bold">{analysisData.PE_Ratio}</span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>P/B Ratio</span>
                        <span className="fw-bold">{analysisData.PB_Ratio}</span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>Beta</span>
                        <span className="fw-bold">{analysisData.Beta}</span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>Dividend Yield</span>
                        <span className="fw-bold">{analysisData.Dividend_Yield}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Analyst Recommendations */}
            <div className="col-lg-4">
              <div className="card h-100 slide-in-right">
                <div className="card-header">
                  <h6 className="d-flex align-items-center mb-0">
                    <Users className="me-2" size={20} />
                    Analyst Consensus
                  </h6>
                </div>
                <div className="card-body text-center">
                  <div className={`badge ${getSignalBadgeClass(analysisData.Analyst_Rating)} fs-4 px-4 py-2 mb-3`}>
                    {analysisData.Analyst_Rating === 'BUY' && <CheckCircle className="me-2" size={20} />}
                    {analysisData.Analyst_Rating === 'SELL' && <AlertTriangle className="me-2" size={20} />}
                    {analysisData.Analyst_Rating === 'HOLD' && <Eye className="me-2" size={20} />}
                    {analysisData.Analyst_Rating}
                  </div>
                  <div className="mb-3 text-muted">Based on {analysisData.Analyst_Count} analysts</div>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>Revenue Growth</span>
                        <span className="fw-bold indicator-positive">{analysisData.Revenue_Growth}</span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <span>Earnings Growth</span>
                        <span className="fw-bold indicator-positive">{analysisData.Earnings_Growth}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 52-Week Range */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="d-flex align-items-center mb-0">
                <Target className="me-2" size={20} />
                52-Week Performance Range
              </h6>
            </div>
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-2 text-start">
                  <small className="text-muted">Low</small>
                  <div className="fw-bold">₹{analysisData['52_Week_Low']}</div>
                </div>
                <div className="col-8">
                  <div className="progress" style={{height: '20px'}}>
                    <div 
                      className="progress-bar bg-gradient" 
                      style={{
                        width: `${((analysisData.Close - analysisData['52_Week_Low']) / (analysisData['52_Week_High'] - analysisData['52_Week_Low'])) * 100}%`
                      }}
                    ></div>
                  </div>
                  <div className="text-center mt-2">
                    <small className="text-muted">Current: ₹{analysisData.Close}</small>
                  </div>
                </div>
                <div className="col-2 text-end">
                  <small className="text-muted">High</small>
                  <div className="fw-bold">₹{analysisData['52_Week_High']}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Price Chart */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="d-flex align-items-center mb-0">
                <BarChart3 className="me-2" size={20} />
                Price Movement (30 Days)
              </h6>
            </div>
            <div className="card-body">
              <div className="chart-container" style={{height: '400px'}}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={tradingHistory}>
                    <defs>
                      <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#667eea" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" />
                    <YAxis domain={['dataMin - 10', 'dataMax + 10']} />
                    <Tooltip 
                      formatter={(value, name) => [`₹${value}`, name === 'close' ? 'Close Price' : name]}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="close" 
                      stroke="#667eea" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorPrice)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Volume Chart */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="d-flex align-items-center mb-0">
                <Volume2 className="me-2" size={20} />
                Volume Analysis (15 Days)
              </h6>
            </div>
            <div className="card-body">
              <div className="chart-container" style={{height: '300px'}}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tradingHistory.slice(-15)}>
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => [formatNumber(value), 'Volume']} />
                    <Bar dataKey="volume" fill="#11998e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="d-flex justify-content-between mt-3">
                <small className="text-muted">Average Volume: {formatNumber(analysisData.Average_Volume)}</small>
                <small className="text-muted">Today's Volume: {formatNumber(analysisData.Volume)}</small>
              </div>
            </div>
          </div>

          {/* Historical Data Table */}
          <div className="card">
            <div className="card-header">
              <h6 className="d-flex align-items-center mb-0">
                <Calendar className="me-2" size={20} />
                Recent Trading History
              </h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="text-end">Open</th>
                      <th className="text-end">High</th>
                      <th className="text-end">Low</th>
                      <th className="text-end">Close</th>
                      <th className="text-end">Volume</th>
                      <th className="text-end">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradingHistory.slice(-10).reverse().map((day, idx) => {
                      const change = day.close - day.open;
                      const changePercent = ((change / day.open) * 100);
                      return (
                        <tr key={idx}>
                          <td className="fw-medium">{day.date}</td>
                          <td className="text-end">₹{day.open}</td>
                          <td className="text-end">₹{day.high}</td>
                          <td className="text-end">₹{day.low}</td>
                          <td className="text-end fw-bold">₹{day.close}</td>
                          <td className="text-end text-muted">{formatNumber(day.volume)}</td>
                          <td className={`text-end fw-bold ${change >= 0 ? 'indicator-positive' : 'indicator-negative'}`}>
                            <div className="d-flex align-items-center justify-content-end">
                              {change >= 0 ? <ArrowUp size={16} className="me-1" /> : <ArrowDown size={16} className="me-1" />}
                              ₹{Math.abs(change).toFixed(2)} ({changePercent.toFixed(2)}%)
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {!selectedStock && !loading && (
        <div className="card text-center py-5">
          <div className="card-body">
            <Search size={64} className="text-muted mb-4" />
            <h3 className="fw-bold mb-3">Get Started with Stock Analysis</h3>
            <p className="text-muted mb-5">Search for any stock to view comprehensive analysis, technical indicators, and professional insights.</p>
            <div className="row g-4">
              <div className="col-md-4">
                <div className="text-center">
                  <BarChart3 size={48} className="text-primary mb-3" />
                  <h5 className="fw-bold">Technical Analysis</h5>
                  <p className="text-muted small">RSI, MACD, Moving Averages</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-center">
                  <DollarSign size={48} className="text-success mb-3" />
                  <h5 className="fw-bold">Valuation Metrics</h5>
                  <p className="text-muted small">P/E, P/B Ratios, Market Cap</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-center">
                  <Users size={48} className="text-info mb-3" />
                  <h5 className="fw-bold">Analyst Insights</h5>
                  <p className="text-muted small">Professional Recommendations</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockAnalyzer;